import { Injectable } from '@angular/core';
import { AppService } from 'src/app/app.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { FetchApiService } from '../core/fetch-api.service';

export interface NameValue {
  name: string;
  value: string;
}

export interface ProductSpecIspData {
  catalogId: string;
  catalogName: string;
  selected: boolean;
  orquestable: boolean;
  externalAPI: boolean
}
export interface ProductSpec {
  productNamingId: string;
  productName: string;
  versions: {
    name: string,
    enabled: boolean
  }[];
  enabled: boolean;
  catalogResponseList: ProductSpecIspData[];
}

export interface ActionsProductSpec {
  productSpecDescription: string;
  productSpecName: string;
  productSpecNamingId: string;
  productSpecVersion: string;
}

export interface ProductOrigin {
  createdAt: Date;
  createdBy: string;
  lastModifiedAt: Date;
  lastModifiedBy: string;
  name: string;
  territoryUser: string;
  code: string;
  idProyectoTicketing: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  appData: any;

  constructor(
    private httpClient: HttpClient,
    appService: AppService,
    private fetchApiService: FetchApiService
  ) {
    this.appData = appService.settings;
  }

  getProducts(params: HttpParams): Observable<any> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/getProducts`, { params });
  }

  getProductsCsv(params: HttpParams): Promise<Response> {
    let mapParams : { [k: string]: string } = {}
    params.keys().forEach(k =>
        mapParams[k] = params.get(k)
      );
    return this.fetchApiService.simpleStreamingRequest(`${this.appData.webApi}/inventory/products/getProductsCsv`,'GET', mapParams);
  }


  getProduct(id: string): Observable<any> {
    const params = new HttpParams().append('id', id);

    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/getProduct`, { params });
  }

  getProductByRFS(name: string): Observable<any> {
    const params = new HttpParams().append('name', name);

    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/getProductByRFS`, { params });
  }

  getProductSpecs(): Observable<NameValue[]> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/getProductSpecs`)
      .pipe(
        map(response => {
          const specIds: NameValue[] = [];

          for (const specId of response._embedded.productSpecs) {
            specIds.push(
              {
                name: specId.name + '_v' + specId.version,
                value: specId.name + '_v' + specId.version
              }
            );
          }

          return specIds;
        })
      );
  }

  getProductSpecs$(): Observable<ActionsProductSpec[]> {
    return this.httpClient.get<ActionsProductSpec[]>(`${this.appData.webApi}/inventory/products/getAllProductSpecsForActions`);
  }

  getIspProductSpecs$(ispCode: string): Observable<ActionsProductSpec[]> {
    return this.httpClient.get<ActionsProductSpec[]>(`${this.appData.webApi}/inventory/products/getProductSpecs/${ispCode}`);
  }

  getPaginatedProductSpecs(page: string, size: string, sort: string): Observable<any> {
    let params = new HttpParams();
    if (page) {
      params = params.append('page', page);
    }
    if (size) {
      params = params.append('size', size);
    }
    if (sort) {
      params = params.append('sort', sort);
    }

    return this.httpClient.get<any>(`${this.appData.webApi}/catalog/productSpecs/search/findAllByEnabledTrue`, { params });
  }

  getProductSpecDetails(name: string): Observable<ProductSpec> {
    return this.httpClient.get<any>(`${this.appData.webApi}/catalog/products/${name}`);
  }

  modifyProductSpec(productSpec: ProductSpec): Observable<any> {
    return this.httpClient.put<any>(`${this.appData.webApi}/catalog/products/modify`, productSpec);
  }

  getCFSSpecs(): Observable<NameValue[]> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/getCFSSpecs`)
      .pipe(
        map(response => {
          const specIds: NameValue[] = [];

          for (const specId of response._embedded.cFSSpecs) {
            specIds.push(
              {
                name: specId.name + '_v' + specId.version,
                value: specId.name + '_v' + specId.version
              }
            );
          }

          return specIds;
        })
      );
  }

  getRFSSpecs(): Observable<NameValue[]> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/getRFSSpecs`)
      .pipe(
        map(response => {
          const specIds: NameValue[] = [];

          for (const specId of response._embedded.rFSSpecs) {
            specIds.push(
              {
                name: specId.name + '_v' + specId.version,
                value: specId.name + '_v' + specId.version
              }
            );
          }

          return specIds;
        })
      );
  }

  getInventoryProduct(id: string): Observable<any> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/product/${id}`);
  }

  getInventoryCFS(id: string): Observable<any> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/cfs/${id}`);
  }

  getInventoryRFS(id: string): Observable<any> {
    return this.httpClient.get<any>(`${this.appData.webApi}/inventory/products/rfs/${id}`);
  }

  getProductOrigin(id: number): Observable<ProductOrigin> {
    return this.httpClient.get<ProductOrigin>(`${this.appData.webApi}/inventory/products/${id}/origin`);
  }

  getTraceabilities(externalOrderId: string): Observable<any> {
    const params = new HttpParams().append('externalOrderId', externalOrderId);
    return this.httpClient.get<any>(`${this.appData.webApi}/traceability-engine/traceability/notification`, { params });
  }

  getTraceability(id: string): Observable<any> {
    return this.httpClient.get<any>(`${this.appData.webApi}/traceability-engine/traceability/notification/${id}`);
  }

}
